import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { FABRICS } from "../data/content";
import { MaskedLine, Reveal } from "../components/Reveal";

const CATS = ["all", "cotton", "silk", "suiting", "print"];

export default function Fabrics() {
  const { lang, t } = useLanguage();
  const hindi = lang === "hi";
  const [cat, setCat] = useState("all");

  const items = cat === "all" ? FABRICS : FABRICS.filter((f) => f.cat === cat);

  return (
    <div className="pt-28 sm:pt-36" data-testid="fabrics-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine delay={0.1}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.fabrics.kicker}</span>
        </MaskedLine>
        <h1 className={`mt-5 leading-[1.02] text-maroon ${hindi ? "font-hindi text-5xl sm:text-7xl" : "font-display text-6xl sm:text-8xl"}`} data-testid="fabrics-title">
          <MaskedLine delay={0.25}>{t.fabrics.title}</MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className={`mt-6 max-w-xl text-base text-ink/60 sm:text-lg ${hindi ? "font-hindi" : ""}`} data-testid="fabrics-subtitle">
            {t.fabrics.text}
          </p>
        </Reveal>

        <Reveal delay={0.55} className="mt-10 flex flex-wrap gap-3">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c}-button`}
              className={`rounded-full border px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors duration-300 ${
                cat === c
                  ? "border-maroon bg-maroon text-ivory"
                  : "border-black/10 bg-white text-ink/60 hover:border-gold hover:text-maroon"
              } ${hindi ? "font-hindi" : ""}`}
            >
              {t.fabrics.filters[c]}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20" data-testid="fabrics-gallery">
        <motion.div layout className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((f, i) => (
              <motion.figure
                layout
                key={f.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative ${i % 5 === 0 ? "lg:row-span-2" : ""}`}
                data-testid={`fabric-card-${f.id}`}
              >
                <div className="img-print h-full overflow-hidden rounded-2xl border border-gold/25">
                  <img
                    src={f.img}
                    alt={hindi ? f.nameHi : f.nameEn}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                      i % 5 === 0 ? "aspect-[3/4] lg:aspect-auto lg:h-full" : "aspect-[4/3]"
                    }`}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className={`text-lg text-white ${hindi ? "font-hindi" : "font-display"}`} data-testid={`fabric-name-${f.id}`}>
                      {hindi ? f.nameHi : f.nameEn}
                    </p>
                    <p className={`mt-0.5 text-xs text-white/75 ${hindi ? "font-hindi" : ""}`}>
                      {hindi ? f.descHi : f.descEn}
                    </p>
                  </figcaption>
                </div>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>
    </div>
  );
}
